import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Star, Heart } from "lucide-react";
import { useSponsorshipTiers } from "@/hooks/useSponsorshipTiers";

export const SponsorTiers = () => {
  const { tiers, loading, error } = useSponsorshipTiers();

  const scrollToForm = (tierId?: string) => {
    const form = document.getElementById('sponsor-form');
    if (form) {
      form.scrollIntoView({ behavior: 'smooth' });
      // Pre-select tier in form if provided
      if (tierId) {
        setTimeout(() => {
          const tierSelect = document.getElementById('tier-select') as HTMLSelectElement;
          if (tierSelect) {
            tierSelect.value = tierId;
            tierSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, 500);
      }
    }
  };

  const getTierIcon = (level: number) => {
    switch (level) {
      case 1: return <Crown className="h-8 w-8 text-yellow-500" />;
      case 2: return <Star className="h-8 w-8 text-blue-500" />;
      case 3: return <Heart className="h-8 w-8 text-green-500" />;
      default: return <Heart className="h-8 w-8 text-gray-500" />;
    }
  };

  const getTierGradient = (level: number) => {
    switch (level) {
      case 1: return "from-yellow-500/20 to-orange-500/20";
      case 2: return "from-blue-500/20 to-purple-500/20";  
      case 3: return "from-green-500/20 to-teal-500/20";
      default: return "from-gray-500/20 to-slate-500/20";
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center">Loading sponsorship tiers...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center text-destructive">Error loading sponsorship tiers</div>
        </div>
      </section>
    );
  }

  return (
    <section id="sponsorship-tiers" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Sponsorship Opportunities
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Choose the sponsorship level that aligns with your organization's values and impact goals.
            All donations are processed in care of Sands Ltd., our registered partner in the Cayman Islands.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {tiers.map((tier) => {
            const benefits = Array.isArray(tier.benefits) ? tier.benefits : [];
            const isPopular = tier.level === 2; // Culture Champion is popular
            
            return (
              <Card 
                key={tier.id} 
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                  isPopular ? 'ring-2 ring-primary shadow-lg' : ''
                }`}
              >
                {isPopular && (
                  <div className="absolute top-4 right-4">
                    <Badge variant="default" className="bg-primary text-primary-foreground">
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <div className={`absolute inset-0 bg-gradient-to-br ${getTierGradient(tier.level)} opacity-50`} />
                
                <CardHeader className="relative text-center pb-4">
                  <div className="flex justify-center mb-4">
                    {getTierIcon(tier.level)}
                  </div>
                  <CardTitle className="text-2xl font-bold">{tier.name}</CardTitle>
                  <div className="text-3xl font-bold text-primary">
                    {tier.currency} {tier.price_min?.toLocaleString()}
                    {tier.price_max && tier.price_max !== tier.price_min && (
                      <span> - {tier.price_max.toLocaleString()}</span>
                    )}
                    {!tier.price_max && tier.level === 1 && <span>+</span>}
                  </div>
                </CardHeader>

                <CardContent className="relative">
                  <ul className="space-y-3 mb-8">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    onClick={() => scrollToForm(tier.id)}
                    className={`w-full ${isPopular ? 'bg-primary hover:bg-primary/90' : ''}`}
                    variant={isPopular ? "default" : "outline"}
                  >
                    Select {tier.name}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground">
            Need a custom sponsorship package?{" "}
            <button 
              onClick={() => scrollToForm()}
              className="text-primary hover:underline font-semibold"
            >
              Contact us for bespoke opportunities
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};